class ArticleReadingNav extends HTMLElement {
  connectedCallback() {
    this.content = document.querySelector(this.dataset.contentSelector);

    if (!this.content) return;

    this.extraContent = Array.from(document.querySelectorAll('[data-reading-content-extra]'));
    this.readingAreas = [this.content, ...this.extraContent];
    this.desktopList = this.querySelector('[data-toc-list]');
    this.mobileList = this.querySelector('[data-toc-list-mobile]');
    this.progressBars = this.querySelectorAll('[data-reading-progress]');
    this.timeLabels = this.querySelectorAll('[data-read-time-label]');
    this.toggle = this.querySelector('[data-toc-toggle]');
    this.drawer = this.querySelector('[data-toc-drawer]');
    this.closeButton = this.querySelector('[data-toc-close]');
    this.headings = this.getHeadings();

    this.setReadTime();
    this.renderTableOfContents();
    this.bindDrawer();
    this.bindScroll();
    this.updateVisibility();
    this.updateProgress();
    this.updateActiveHeading();
  }

  getHeadings() {
    const headings = [
      ...Array.from(this.content.querySelectorAll('h2, h3')),
      ...Array.from(document.querySelectorAll('[data-article-toc-heading]')),
    ];

    if (headings.length > 0) {
      headings.forEach((heading) => {
        if (!heading.id) heading.id = this.createHeadingId(heading);
      });

      return headings;
    }

    const title = document.querySelector('.article-template__title');

    if (!title) return [];
    if (!title.id) title.id = 'article-title';

    return [title];
  }

  createHeadingId(heading) {
    const baseId =
      heading.textContent
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9\u4e00-\u9fff]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'section';
    let id = baseId;
    let count = 2;

    while (document.getElementById(id)) {
      id = `${baseId}-${count}`;
      count += 1;
    }

    return id;
  }

  setReadTime() {
    const customLabel = this.dataset.readTimeLabel?.trim();
    const label =
      customLabel && /^\d+$/.test(customLabel) ? `${parseInt(customLabel, 10)} min read` : customLabel || `${this.getReadTimeMinutes()} min read`;

    this.timeLabels.forEach((timeLabel) => {
      timeLabel.textContent = label;
    });
  }

  getReadTimeMinutes() {
    const customReadTime = parseInt(this.dataset.readTime, 10);

    if (customReadTime > 0) return customReadTime;

    const text = this.content.textContent.trim();
    const cjkCharacters = text.match(/[\u4e00-\u9fff]/g)?.length || 0;
    const words = text.replace(/[\u4e00-\u9fff]/g, '').trim().split(/\s+/).filter(Boolean).length;
    const estimatedMinutes = Math.ceil(Math.max(cjkCharacters / 450, words / 200));

    return Math.max(1, estimatedMinutes);
  }

  renderTableOfContents() {
    const links = this.headings.map((heading) => {
      const link = document.createElement('a');
      const level = heading.tagName.toLowerCase();

      link.className = `article-reading-nav__link article-reading-nav__link--${level}`;
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();

      link.addEventListener('click', () => {
        this.closeDrawer();
      });

      return link;
    });

    [this.desktopList, this.mobileList].forEach((list) => {
      links.forEach((link) => list.appendChild(link.cloneNode(true)));
    });

    this.mobileList.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => this.closeDrawer());
    });
  }

  bindDrawer() {
    this.toggle?.addEventListener('click', () => {
      const shouldOpen = this.drawer.hidden;

      this.drawer.hidden = !shouldOpen;
      this.toggle.setAttribute('aria-expanded', String(shouldOpen));
    });

    this.closeButton?.addEventListener('click', () => this.closeDrawer());
  }

  closeDrawer() {
    if (!this.drawer || !this.toggle) return;

    this.drawer.hidden = true;
    this.toggle.setAttribute('aria-expanded', 'false');
  }

  bindScroll() {
    window.addEventListener('scroll', () => {
      window.requestAnimationFrame(() => {
        this.updateVisibility();
        this.updateProgress();
        this.updateActiveHeading();
      });
    });
  }

  updateVisibility() {
    const isReadingContent = this.readingAreas.some((readingArea) => {
      const areaRect = readingArea.getBoundingClientRect();

      return areaRect.top < window.innerHeight * 0.72 && areaRect.bottom > window.innerHeight * 0.28;
    });

    this.classList.toggle('is-reading-content', isReadingContent);

    if (!isReadingContent) this.closeDrawer();
  }

  updateProgress() {
    const contentRect = this.content.getBoundingClientRect();
    const lastArea = this.readingAreas[this.readingAreas.length - 1];
    const lastAreaRect = lastArea.getBoundingClientRect();
    const contentTop = window.scrollY + contentRect.top;
    const contentBottom = window.scrollY + lastAreaRect.bottom;
    const contentHeight = contentBottom - contentTop - window.innerHeight;
    const progress = Math.min(Math.max((window.scrollY - contentTop) / contentHeight, 0), 1) || 0;

    this.progressBars.forEach((progressBar) => {
      if (progressBar.classList.contains('article-reading-nav__mobile-progress')) {
        progressBar.style.width = `${progress * 100}%`;
      } else {
        progressBar.style.height = `${progress * 100}%`;
      }
    });
  }

  updateActiveHeading() {
    const activeHeading = [...this.headings]
      .reverse()
      .find((heading) => heading.getBoundingClientRect().top <= 140);

    if (!activeHeading) return;

    this.querySelectorAll('.article-reading-nav__link').forEach((link) => {
      link.classList.toggle('is-active', link.getAttribute('href') === `#${activeHeading.id}`);
    });
  }
}

customElements.define('article-reading-nav', ArticleReadingNav);
